import React, { useState, useRef, useEffect } from 'react';
import { inventoryApi } from '../services/api';
import { useUIActions } from '../store';
import LoadingSpinner from './LoadingSpinner';
import CameraScanner from './CameraScanner';

interface ScanResult {
  success: boolean;
  batch_id?: string;
  product_name?: string;
  message: string;
  match_type?: string;
  vendor_code?: string;
  serial_number?: string;
  imei?: string;
  timestamp?: string;
}

interface ScanHistory {
  id: string;
  timestamp: string;
  result: ScanResult;
  input: {
    po_id: string;
    serial_number?: string;
    imei?: string;
  };
}

const InventoryPage: React.FC = () => {
  const [serialNumber, setSerialNumber] = useState('');
  const [imei, setImei] = useState('');
  const [poId, setPoId] = useState('');
  const [loading, setLoading] = useState(false);
  const [lastResult, setLastResult] = useState<ScanResult | null>(null);
  const [scanHistory, setScanHistory] = useState<ScanHistory[]>([]);
  const [scanMode, setScanMode] = useState<'manual' | 'continuous'>('manual');
  const [isListening, setIsListening] = useState(false);
  const [purchaseOrders, setPurchaseOrders] = useState<any[]>([]);
  const [todayStats, setTodayStats] = useState({ scanned: 0, matched: 0, unmatched: 0 });
  const [showCameraScanner, setShowCameraScanner] = useState(false);
  const { addToast } = useUIActions();

  const serialInputRef = useRef<HTMLInputElement>(null);
  const imeiInputRef = useRef<HTMLInputElement>(null);
  const continuousScanIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // 加載掃描歷史
    loadScanHistory();
    
    // 加載採購單資料
    loadPurchaseOrders();
    
    // 加載今日統計
    loadTodayStats();
    
    // 自動聚焦到序號輸入框
    if (serialInputRef.current) {
      serialInputRef.current.focus();
    }

    return () => {
      if (continuousScanIntervalRef.current) {
        clearInterval(continuousScanIntervalRef.current);
      }
    };
  }, []);

  const loadScanHistory = () => {
    const saved = localStorage.getItem('scan_history');
    if (saved) {
      try {
        setScanHistory(JSON.parse(saved));
      } catch (error) {
        console.error('載入掃描歷史失敗:', error);
      }
    }
  };

  const saveScanHistory = (newHistory: ScanHistory[]) => {
    localStorage.setItem('scan_history', JSON.stringify(newHistory.slice(0, 50))); // 保留最近50條記錄
  };

  const loadPurchaseOrders = async () => {
    try {
      // 載入採購單資料 (排除已完成的訂單)
      const poResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/purchase-orders`);
      if (poResponse.ok) {
        const poData = await poResponse.json();
        if (poData.success) {
          // 過濾掉已完成的採購單
          const activePOs = poData.data.filter((po: any) => po.status !== 'completed');
          setPurchaseOrders(activePOs);
          // 自動選擇最新的採購單
          if (activePOs.length > 0) {
            setPoId(activePOs[0].po_id);
          }
        }
      }
    } catch (error) {
      console.error('載入採購單資料失敗:', error);
    }
  };

  const loadTodayStats = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/reports/dashboard`);
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setTodayStats({
            scanned: data.data.today_stats.total_scanned,
            matched: data.data.today_stats.matched,
            unmatched: data.data.today_stats.unmatched
          });
        }
      }
    } catch (error) {
      console.error('載入今日統計失敗:', error);
    }
  };

  const handleScan = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    
    if (!serialNumber && !imei) {
      addToast({
        type: 'warning',
        title: '輸入不完整',
        message: '請輸入序號或IMEI'
      });
      return;
    }

    if (!poId) {
      addToast({
        type: 'warning',
        title: '請選擇採購單',
        message: '請先選擇要進行點貨的採購單'
      });
      return;
    }

    // 找到當前選中的採購單來取得 vendor_code
    const currentPO = purchaseOrders.find(po => po.po_id === poId);
    if (!currentPO) {
      addToast({
        type: 'error',
        title: '採購單錯誤',
        message: '找不到選擇的採購單資料'
      });
      return;
    }

    setLoading(true);
    const scanData = {
      po_id: poId,
      vendor_code: currentPO.vendor_code,
      serial_number: serialNumber || undefined,
      imei: imei || undefined
    };

    try {
      const response = await inventoryApi.scan(scanData);

      if (response.success && response.data) {
        const result: ScanResult = {
          ...response.data,
          timestamp: new Date().toISOString()
        };
        
        setLastResult(result);

        // 添加到掃描歷史
        const newHistoryItem: ScanHistory = {
          id: Date.now().toString(),
          timestamp: new Date().toISOString(),
          result,
          input: scanData
        };

        const newHistory = [newHistoryItem, ...scanHistory];
        setScanHistory(newHistory);
        saveScanHistory(newHistory);

        if (result.success) {
          addToast({
            type: 'success',
            title: '✅ 掃描成功',
            message: result.message
          });

          // 播放成功音效 (如果支援)
          if (window.AudioContext) {
            playSuccessSound();
          }
        } else {
          addToast({
            type: 'warning',
            title: '⚠️ 未找到匹配',
            message: result.message
          });

          // 播放警告音效 (如果支援)
          if (window.AudioContext) {
            playWarningSound();
          }
        }
        
        // 更新統計資料
        setTodayStats(prev => ({
          scanned: prev.scanned + 1,
          matched: prev.matched + (result.success ? 1 : 0),
          unmatched: prev.unmatched + (result.success ? 0 : 1)
        }));
        
        // 清空輸入欄位並聚焦
        setSerialNumber('');
        setImei('');
        if (serialInputRef.current) {
          serialInputRef.current.focus();
        }
      }
    } catch (error: any) {
      console.error('掃描錯誤:', error);
      
      const result: ScanResult = {
        success: false,
        message: error.response?.data?.message || '系統發生錯誤，請稍後再試',
        timestamp: new Date().toISOString()
      };
      
      setLastResult(result);
      
      addToast({
        type: 'error',
        title: '掃描失敗',
        message: result.message
      });
    } finally {
      setLoading(false);
    }
  };

  const playSuccessSound = () => {
    try {
      const audioContext = new AudioContext();
      
      // 建立兩個音符來產生成功的"叮咚"聲
      const playNote = (frequency: number, startTime: number, duration: number) => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(frequency, startTime);
        
        gainNode.gain.setValueAtTime(0, startTime);
        gainNode.gain.linearRampToValueAtTime(0.3, startTime + 0.01);
        gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
        
        oscillator.start(startTime);
        oscillator.stop(startTime + duration);
      };
      
      // 播放兩個上升的音符 (成功音效)
      playNote(523, audioContext.currentTime, 0.15); // C5
      playNote(659, audioContext.currentTime + 0.1, 0.2); // E5
      
    } catch (error) {
      // 忽略音效播放失敗
    }
  };

  const playWarningSound = () => {
    try {
      const audioContext = new AudioContext();
      
      // 建立警告音效 (低沉的嗡嗡聲)
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // 使用方波產生較粗糙的警告聲
      oscillator.type = 'square';
      oscillator.frequency.setValueAtTime(200, audioContext.currentTime);
      oscillator.frequency.linearRampToValueAtTime(150, audioContext.currentTime + 0.2);
      
      gainNode.gain.setValueAtTime(0, audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.15, audioContext.currentTime + 0.02);
      gainNode.gain.setValueAtTime(0.15, audioContext.currentTime + 0.1);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
      
      oscillator.start();
      oscillator.stop(audioContext.currentTime + 0.3);
      
    } catch (error) {
      // 忽略音效播放失敗
    }
  };

  const handleContinuousScan = () => {
    if (scanMode === 'continuous') {
      setScanMode('manual');
      setIsListening(false);
      if (continuousScanIntervalRef.current) {
        clearInterval(continuousScanIntervalRef.current);
      }
    } else {
      setScanMode('continuous');
      setIsListening(true);
      // 設定連續掃描模式（每2秒檢查一次輸入）
      continuousScanIntervalRef.current = setInterval(() => {
        if ((serialNumber || imei) && !loading) {
          handleScan();
        }
      }, 2000);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !loading) {
      e.preventDefault();
      handleScan();
    }
  };

  const clearHistory = () => {
    setScanHistory([]);
    localStorage.removeItem('scan_history');
    addToast({
      type: 'success',
      title: '清除成功',
      message: '掃描歷史已清除'
    });
  };

  const retryLastScan = () => {
    if (scanHistory.length > 0) {
      const lastScan = scanHistory[0];
      setSerialNumber(lastScan.input.serial_number || '');
      setImei(lastScan.input.imei || '');
      setTimeout(() => handleScan(), 100);
    }
  };

  const handleCameraScan = (result: string, type: 'qr' | 'ocr') => {
    // 自動填入掃描結果到序號欄位
    setSerialNumber(result);
    
    // 顯示掃描類型提示
    addToast({
      type: 'info',
      title: `${type === 'qr' ? 'QR碼' : 'OCR文字'}識別成功`,
      message: `已識別序號: ${result}`
    });
    
    // 自動執行掃描
    setTimeout(() => {
      handleScan();
    }, 500);
  };

  const ResultCard = ({ result }: { result: ScanResult }) => (
    <div className={`rounded-xl p-6 border-2 ${
      result.success 
        ? 'bg-green-50 border-green-200' 
        : 'bg-yellow-50 border-yellow-200'
    }`}>
      <div className="flex items-start">
        <div className="flex-shrink-0">
          {result.success ? (
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <svg className="h-6 w-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
          ) : (
            <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
              <svg className="h-6 w-6 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
          )}
        </div>
        <div className="ml-4 flex-1">
          <h3 className={`text-lg font-semibold ${
            result.success ? 'text-green-800' : 'text-yellow-800'
          }`}>
            {result.success ? '匹配成功 ✅' : '未找到匹配 ⚠️'}
          </h3>
          <p className={`mt-1 text-sm ${
            result.success ? 'text-green-700' : 'text-yellow-700'
          }`}>
            {result.message}
          </p>
          {result.batch_id && (
            <div className="mt-3 space-y-2">
              <div className="bg-white bg-opacity-50 rounded-lg p-3">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-600">批號:</span>
                    <p className="font-mono text-lg font-bold text-gray-900">{result.batch_id}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-600">匹配方式:</span>
                    <p className="font-medium">{result.match_type === 'serial' ? '序號匹配' : 'IMEI匹配'}</p>
                  </div>
                </div>
                <div className="mt-2">
                  <span className="font-medium text-gray-600">商品:</span>
                  <p className="text-gray-900">{result.product_name}</p>
                </div>
              </div>
            </div>
          )}
          {result.timestamp && (
            <p className="mt-2 text-xs text-gray-500">
              掃描時間: {new Date(result.timestamp).toLocaleString('zh-TW')}
            </p>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">點貨作業</h1>
          <p className="mt-1 text-sm text-gray-500">
            掃描或輸入商品資訊進行自動匹配
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <div className={`px-3 py-1 rounded-full text-xs font-medium ${
            scanMode === 'continuous' 
              ? 'bg-green-100 text-green-800' 
              : 'bg-gray-100 text-gray-800'
          }`}>
            {scanMode === 'continuous' ? '連續掃描模式' : '手動掃描模式'}
          </div>
          <button
            onClick={handleContinuousScan}
            className={`inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 ${
              scanMode === 'continuous'
                ? 'text-white bg-red-600 hover:bg-red-700 border-red-600'
                : 'text-gray-700 bg-white hover:bg-gray-50'
            }`}
          >
            {scanMode === 'continuous' ? (
              <>
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 10l2 2 4-4" />
                </svg>
                停止連續掃描
              </>
            ) : (
              <>
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                開始連續掃描
              </>
            )}
          </button>
        </div>
      </div>

      {/* 今日統計 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="p-2 rounded-lg bg-blue-100">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">今日掃描</p>
              <p className="text-xl font-bold text-gray-900">{todayStats.scanned}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="p-2 rounded-lg bg-green-100">
              <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">成功匹配</p>
              <p className="text-xl font-bold text-green-600">{todayStats.matched}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="p-2 rounded-lg bg-yellow-100">
              <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">未匹配</p>
              <p className="text-xl font-bold text-yellow-600">{todayStats.unmatched}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* 點貨表單 */}
        <div className="lg:col-span-2">
          <div className="bg-white shadow-lg rounded-xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-900">掃描商品</h2>
              {isListening && (
                <div className="flex items-center text-green-600">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></div>
                  <span className="text-sm font-medium">監聽中...</span>
                </div>
              )}
            </div>
            
            <form onSubmit={handleScan} className="space-y-6">
              {/* 採購單選擇 - 必填 */}
              <div>
                <label htmlFor="po" className="block text-sm font-medium text-gray-700 mb-2">
                  選擇採購單 <span className="text-red-500">*</span>
                </label>
                <select
                  id="po"
                  value={poId}
                  onChange={(e) => setPoId(e.target.value)}
                  required
                  className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-base"
                >
                  <option value="">請選擇採購單</option>
                  {purchaseOrders.map(po => (
                    <option key={po.po_id} value={po.po_id}>
                      {po.po_id} - {po.vendor_name} ({po.total_items}項商品)
                    </option>
                  ))}
                </select>
                {purchaseOrders.length === 0 ? (
                  <p className="mt-1 text-xs text-yellow-600">
                    ⚠️ 目前沒有可用的採購單，請先上傳對帳單
                  </p>
                ) : (
                  <p className="mt-1 text-xs text-gray-500">
                    請選擇要進行點貨的採購單，只會顯示未完成的訂單
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="serial" className="block text-sm font-medium text-gray-700 mb-2">
                    序號
                  </label>
                  <div className="relative">
                    <input
                      ref={serialInputRef}
                      type="text"
                      id="serial"
                      value={serialNumber}
                      onChange={(e) => setSerialNumber(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="請輸入或掃描序號"
                      className="block w-full pl-4 pr-20 py-3 border border-gray-300 rounded-lg shadow-sm focus:border-primary-500 focus:ring-primary-500 text-base"
                    />
                    <div className="absolute inset-y-0 right-0 flex items-center">
                      <button
                        type="button"
                        onClick={() => setShowCameraScanner(true)}
                        className="mr-2 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                        title="開啟相機掃描"
                      >
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </button>
                      <div className="pr-3 flex items-center pointer-events-none">
                        <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V6a1 1 0 00-1-1H5a1 1 0 00-1 1v1a1 1 0 001 1z" />
                        </svg>
                      </div>
                    </div>
                  </div>
                  <div className="mt-2 bg-blue-50 rounded-lg p-3">
                    <p className="text-xs text-blue-700 font-medium">
                      💡 測試序號:
                    </p>
                    <div className="mt-1 space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="font-mono bg-white px-2 py-1 rounded text-xs">24081604057</span>
                        <button
                          type="button"
                          onClick={() => setSerialNumber('24081604057')}
                          className="text-xs text-blue-600 hover:text-blue-800"
                        >
                          使用
                        </button>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="font-mono bg-white px-2 py-1 rounded text-xs">C6KZDG3MN73D</span>
                        <button
                          type="button"
                          onClick={() => setSerialNumber('C6KZDG3MN73D')}
                          className="text-xs text-blue-600 hover:text-blue-800"
                        >
                          使用
                        </button>
                      </div>
                    </div>
                    <p className="text-xs text-blue-600 mt-2">這些序號會成功匹配，可用於測試</p>
                  </div>
                </div>

                <div>
                  <label htmlFor="imei" className="block text-sm font-medium text-gray-700 mb-2">
                    IMEI
                  </label>
                  <div className="relative">
                    <input
                      ref={imeiInputRef}
                      type="text"
                      id="imei"
                      value={imei}
                      onChange={(e) => setImei(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="請輸入或掃描IMEI"
                      className="block w-full pl-4 pr-10 py-3 border border-gray-300 rounded-lg shadow-sm focus:border-primary-500 focus:ring-primary-500 text-base"
                    />
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                    </div>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    15位數字識別碼
                  </p>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setSerialNumber('');
                    setImei('');
                    if (serialInputRef.current) {
                      serialInputRef.current.focus();
                    }
                  }}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  清除
                </button>
                <button
                  type="submit"
                  disabled={loading || (!serialNumber && !imei)}
                  className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
                >
                  {loading ? (
                    <LoadingSpinner size="small" className="mr-2" />
                  ) : (
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  )}
                  {loading ? '掃描中...' : '開始掃描'}
                </button>
              </div>
            </form>
          </div>

          {/* 掃描結果 */}
          {lastResult && (
            <div className="mt-6">
              <ResultCard result={lastResult} />
            </div>
          )}
        </div>

        {/* 掃描歷史 */}
        <div className="lg:col-span-1">
          <div className="bg-white shadow-lg rounded-xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">掃描歷史</h3>
              <div className="flex space-x-2">
                {scanHistory.length > 0 && (
                  <button
                    onClick={retryLastScan}
                    className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                  >
                    重試
                  </button>
                )}
                <button
                  onClick={clearHistory}
                  className="text-xs text-red-600 hover:text-red-700 font-medium"
                >
                  清除
                </button>
              </div>
            </div>
            
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {scanHistory.length === 0 ? (
                <div className="text-center py-8">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                  </svg>
                  <p className="mt-2 text-sm text-gray-500">尚無掃描記錄</p>
                </div>
              ) : (
                scanHistory.map((item) => (
                  <div key={item.id} className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50 transition-colors duration-150">
                    <div className="flex items-center justify-between mb-2">
                      <div className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                        item.result.success 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {item.result.success ? '成功' : '未匹配'}
                      </div>
                      <span className="text-xs text-gray-500">
                        {new Date(item.timestamp).toLocaleTimeString('zh-TW', {
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit'
                        })}
                      </span>
                    </div>
                    <div className="text-sm">
                      <p className="text-gray-900 truncate">{item.result.message}</p>
                      {item.result.batch_id && (
                        <p className="text-xs font-mono text-gray-600 mt-1">
                          批號: {item.result.batch_id}
                        </p>
                      )}
                      <p className="text-xs text-gray-500 mt-1">
                        採購單: {item.input.po_id} | 
                        {item.input.serial_number ? ` 序號: ${item.input.serial_number}` : ''}
                        {item.input.imei ? ` IMEI: ${item.input.imei}` : ''}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 使用說明 */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
        <h3 className="text-lg font-semibold text-blue-900 mb-4 flex items-center">
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          使用說明
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-blue-800">
          <div className="space-y-2">
            <p>• 請先選擇要進行點貨的採購單</p>
            <p>• 系統只會顯示未完成的採購單供選擇</p>
            <p>• 輸入序號或IMEI進行商品掃描和匹配</p>
          </div>
          <div className="space-y-2">
            <p>• 連續掃描模式適合大量商品處理</p>
            <p>• 支援掃描槍直接輸入，按Enter鍵即可掃描</p>
            <p>• 測試時可使用序號 "C6KZDG3MN73D" 體驗成功匹配</p>
          </div>
        </div>
      </div>

      {/* 相機掃描器 */}
      <CameraScanner
        isOpen={showCameraScanner}
        onClose={() => setShowCameraScanner(false)}
        onScan={handleCameraScan}
        placeholder="掃描序號或QR碼"
      />
    </div>
  );
};

export default InventoryPage;